// ONE WORDING FOR A MAILBOX'S STATE (Carla, 2026-10-21).
//
// Emails said "Outlook (...) did not load. Your connection may have expired." while Settings → Email
// connections said "Connected". The server now has one answer per mailbox (Aidey Backend/
// lib/mailboxHealth.js): GET /api/connections/email?health=1 carries it for Settings, and the inbox's
// failedConnections carries the SAME record for the Emails banner. This file is the one place that
// answer is put into words, and the one place Reconnect goes, so the banner and Settings cannot drift.
import { startOAuth } from './oauthStart';
import { formatDateTime } from './formatting';

export const mailboxName = (conn) =>
  conn?.provider === 'gmail' ? 'Gmail' : conn?.provider === 'outlook' ? 'Outlook' : (conn?.label || conn?.email || 'This mailbox');

// The pill beside a mailbox: { tone, text } for StatusPill, and the line under it.
export function healthPill(health) {
  switch (health?.state) {
    case 'connected': return { tone: 'success', text: 'Connected' };
    case 'needs_reconnecting': return { tone: 'warning', text: 'Needs reconnecting' };
    case 'unreachable': return { tone: 'pending', text: 'Did not load just now' };
    case 'failed': return { tone: 'warning', text: 'Could not load' };
    default: return { tone: 'neutral', text: 'Not checked yet' };
  }
}

export function healthDetail(health) {
  if (!health) return null;
  if (health.state === 'connected') return health.lastOkAt ? `Last synced ${formatDateTime(health.lastOkAt)}` : null;
  if (health.state === 'unknown') return 'Aidey has not used this mailbox since the server last started.';
  return health.reason || null;
}

export const needsAttention = (health) => ['needs_reconnecting', 'unreachable', 'failed'].includes(health?.state);

// The Emails banner's sentence, from the server's three kinds (Carla, 2026-10-22):
//   needs_reconnecting  "Needs reconnecting: <reason>", with Reconnect (a real sign-in failure only)
//   unreachable         "Microsoft unreachable: ..." / "Google unreachable: ..." (timeouts, network, 5xx)
//   failed              Aidey could not load it, and we know; no Reconnect (reconnecting would not fix it)
// `tried` is when it was last tried, said after a Try again that failed.
export function bannerText(conn, { tried = false } = {}) {
  const h = conn?.health;
  const who = `${mailboxName(conn)} (${conn?.email})`;
  const when = tried && h?.checkedAt ? ` Tried again at ${formatDateTime(h.checkedAt)}.` : '';
  if (h?.state === 'needs_reconnecting') return `${who}. Needs reconnecting: ${h.reason || 'its sign-in is no longer accepted.'}${when}`;
  if (h?.state === 'unreachable') return `${who}. ${h.reason || 'Unreachable just now.'}${when}`;
  return `${who}. ${h?.reason || 'Aidey couldn’t load this mailbox. We’ve been told and are looking into it.'}${when}`;
}

// Which button a banner has: Reconnect (or Edit for IMAP) only for a real sign-in failure; otherwise
// Try again.
export const bannerAction = (conn) => (conn?.health?.state === 'needs_reconnecting' ? 'fix' : 'retry');

// What fixes it: an OAuth mailbox reconnects (the existing connect for THAT provider, opened on the
// stored account, returning to Settings → Email connections); an IMAP mailbox is edited.
export const fixLabel = (conn) => (conn?.provider === 'imap' ? 'Edit in Settings' : 'Reconnect');

export function reconnectMailbox(conn) {
  if (conn?.provider === 'gmail') return startOAuth('google', { reconnect: true });
  if (conn?.provider === 'outlook') return startOAuth('microsoft', { reconnect: true });
  window.dispatchEvent(new CustomEvent('aidey:goto-settings', { detail: 'email' }));
  return Promise.resolve();
}
